import { useId, type ReactNode } from 'react';
import { makeStyles, mergeClasses, tokens, Text } from '@coreai/fluentui-react';

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    // Whitespace alone separates this section from the page-level header
    // (or a previous section). The vertical rhythm reinforces hierarchy
    // without introducing another strong visual boundary.
    marginTop: tokens.spacingVerticalXXXL,
    // Claim the remaining vertical space inside the page so that any child
    // with `overflow: auto` (tables, tab panels, etc.) can actually scroll
    // within the section instead of pushing the whole page taller and
    // forcing the layout's outer scrollbar to take over. Pairing
    // `flex: 1` with `minHeight: 0` lets the section shrink below its
    // content height so inner scrollers bound correctly inside flex
    // column page layouts.
    flex: 1,
    minHeight: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
  },
  iconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground2,
    // Optical alignment: nudge the icon down using the smallest vertical
    // spacing token so it centers on the heading cap-height rather than
    // the heading's flex line-box.
    paddingTop: tokens.spacingVerticalXXS,
    flexShrink: 0,
  },
  titleBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
    flex: 1,
  },
  titleLine: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  description: {
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase300,
  },
  actions: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
});

export interface PageSectionProps {
  /** Section title — rendered as an `<h2>` and used to label the section. */
  title: string;
  /** Optional interactive or status content rendered beside, not inside, the heading. */
  titleAccessory?: ReactNode;
  /**
   * Optional supporting copy shown directly under the title. Use when the
   * section needs a short purpose statement (e.g., "Add knowledge sources
   * to build a knowledge base…"). Keep to one or two sentences.
   */
  description?: ReactNode;
  /**
   * Optional leading icon. Sized by the caller (`<SettingsRegular />`,
   * `<Library24Regular />`, etc.) so it inherits the icon scale used
   * elsewhere on the page.
   */
  icon?: ReactNode;
  /**
   * Optional right-aligned slot, typically for primary actions tied to the
   * section ("Add file", "Create app"). Renders alongside the title row.
   */
  actions?: ReactNode;
  /** Section body. */
  children?: ReactNode;
  /**
   * Optional override id for the heading. By default a stable id is
   * generated via `useId()` and used to wire `aria-labelledby` on the
   * section. Provide one only when an outer component needs to reference
   * the heading directly.
   */
  headingId?: string;
  /** Extra class merged onto the outer `<section>`. */
  className?: string;
}

/**
 * A section of a page introduced by an `<h2>` heading.
 *
 * `PageSection` is the standard "page subheader" used after the
 * page-level `<ProjectOverviewHeader>` (or `<AppOverviewHeader>`). It
 * provides:
 *
 *  - semantic `<section aria-labelledby={…}>` framing,
 *  - a single consistent visual treatment for the heading row across
 *    the portal (so Settings, Knowledge, Sandboxes, Apps, etc. don't
 *    each invent their own header bar), and
 *  - composable slots for an icon, description, and right-aligned
 *    actions — composition over configuration.
 *
 * Layout contract: the root `<section>` uses `flex: 1; minHeight: 0` so
 * children with `overflow: auto` can scroll inside the section rather
 * than expanding the page. **Consumers must render `PageSection` inside
 * a `flex-direction: column` parent that has a bounded height** (e.g.,
 * `100%` or `100vh` minus header). Rendering inside a non-flex parent
 * is harmless but the inner scroll behaviour will fall back to the
 * outer page scrollbar.
 */
export const PageSection = ({
  title,
  titleAccessory,
  description,
  icon,
  actions,
  children,
  headingId,
  className,
}: PageSectionProps) => {
  const styles = useStyles();
  const autoId = useId();
  const id = headingId ?? autoId;

  return (
    <section className={mergeClasses(styles.section, className)} aria-labelledby={id}>
      <div className={styles.header}>
        {icon && (
          <span className={styles.iconWrap} aria-hidden="true">
            {icon}
          </span>
        )}
        <div className={styles.titleBlock}>
          <div className={styles.titleLine}>
            <Text as="h2" id={id} className={styles.title}>
              {title}
            </Text>
            {titleAccessory}
          </div>
          {description && (
            <Text as="p" size={200} className={styles.description}>
              {description}
            </Text>
          )}
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {children}
    </section>
  );
};
